import type { EventType } from "@/pages/calendar/components/utils/utils";
import axios from "@/utils/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface CreateEventBody {
  id?: string;
  start_date: string | null;
  end_date: string | null;
  name: string;
  description: string;
  type: string;
  recurring: boolean;
  recurrence_type?: string | null;
  active: boolean;
  // repeat_type: "HOLIDAY" | "NON_WORKING_DAY" | "SPECIAL_EVENT";
  recurrence_meta?: {
    month?: number | null;
    week?: number | null;
    weekdays?: number[] | null;
  } | null;
}

export const useGetCalendarEvents = (
  rows: Array<Date[]>,
  relationships: string[],
) => {
  const start_date = rows[0][0].toISOString().split("T")[0];
  const end_date = rows[4][6].toISOString().split("T")[0];

  const filterParams = {
    ...(start_date && { start_date }),
    ...(end_date && { end_date }),
  };

  const queryKey = {
    ...filterParams,
    relationships,
  };

  return useQuery({
    queryKey: ["calendar-events", queryKey],
    queryFn: async (): Promise<EventType[]> => {
      const response = await axios.get("admin/v1/calendar", {
        params: {
          filter: {
            ...filterParams,
            active: true,
          },
          ...(relationships.length > 0 && {
            relationships: relationships.join(","),
          }),
        },
      });
      return response.data.data;
    },
  });
};

export const useCreateEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateEventBody) => {
      const response = await axios.post("admin/v1/calendar", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["calendar-events"],
      });
    },
  });
};

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateEventBody) => {
      const response = await axios.patch(`admin/v1/calendar/${data.id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["calendar-events"],
      });
    },
  });
};

export const useDeleteEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: string) => {
      const response = await axios.delete(`admin/v1/calendar/${eventId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["calendar-events"],
      });
    },
  });
};

export const useGetCurrentFiscalYear = () => {
  return useQuery({
    queryKey: ["current-fiscal-year"],
    queryFn: async (): Promise<IFiscalYear> => {
      const response = await axios.get("admin/v1/fiscal-year/current");
      return response.data.data;
    },
  });
};

export const useCreateFiscalYear = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      id?: string;
      name: string;
      start_date: string;
      end_date: string;
    }) => {
      const response = await axios.post(`admin/v1/fiscal-year`, {
        ...data,
        notes: data?.id && `Actualización del año fiscal ${data.start_date}`,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["current-fiscal-year"],
      });
    },
  });
};

export interface IFiscalYear {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  created_by: string;
  created_by_user: CreatedByUser;
  changes: Changes[];
  is_active: boolean;
  has_started: boolean;
  has_ended: boolean;
  duration_in_days: number;
  days_left: number;
  created_at: Date;
  updated_at: Date;
}

export interface CreatedByUser {
  id: string;
  name: string;
  email: string;
}

export interface Changes {
  id: string;
  notes: string;
  details: Details;
  updated_by: string;
  created_at: Date;
}

export interface Details {
  start_month: Start;
  start_day: Start;
}

export interface Start {
  old: number;
  new: number;
}
